import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ShoppingCart, User, LogOut, Menu, X, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePublicSettings } from "@/lib/publicSettings";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "Browse Plans", to: "/plans" },
  { label: "Floor Plans", to: "/floor-plans" },
  { label: "Plan Collections", to: "/plan-collections" },
  { label: "Custom Design", to: "/custom-design" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

const isStaff = (role) => role && role !== "customer";

const Header = () => {
  const { user, logout, cart, wishlist, currency, setCurrency } = useApp();
  const { settings } = usePublicSettings();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const brandName = settings?.company?.name || "Dream Home Engineers";
  const brandTagline = settings?.company?.tagline || "Design · Plan · Build · Deliver";
  // Iter48 Phase C — new premium logo shipped as the default. Admin can still
  // override via settings.company.logo_url (or upcoming Branding Manager).
  const logoUrl = settings?.company?.logo_url || "/dhe-logo.png";
  // Iter54a — Admin-configurable header/logo sizing from Branding CMS. All
  // values are CSS strings so any unit works (px / rem / %). Empty values
  // fall back to sensible defaults so existing sites don't break.
  const cmpny = settings?.company || {};
  const headerHeight = cmpny.header_height || "80px";
  const logoWidthDesktop = cmpny.header_logo_width || "";
  const logoWidthMobile = cmpny.mobile_logo_width || "";
  const logoDisplayMode = cmpny.logo_display_mode || "contain"; // contain | cover | fill | scale-down
  const logoStyle = {
    height: "100%",
    maxHeight: headerHeight,
    objectFit: logoDisplayMode,
  };
  // Apply width via custom property so we can drive desktop / mobile from CSS.
  const rowStyle = {
    "--dhe-header-height": headerHeight,
    "--dhe-logo-w-desktop": logoWidthDesktop || "auto",
    "--dhe-logo-w-mobile": logoWidthMobile || logoWidthDesktop || "auto",
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border"
    >
      <div
        className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between gap-3 sm:gap-6 dhe-header-row"
        style={rowStyle}
      >
        <Link to="/" className="flex items-center gap-3 min-w-0 md:shrink-0 h-full py-2" data-testid="brand-link">
          {logoUrl ? (
            // Iter54a — logo dimensions now driven by CMS. `dhe-brand-logo`
            // class picks up the desktop/mobile width from CSS variables.
            <img
              src={logoUrl}
              alt={brandName}
              className="dhe-brand-logo w-auto shrink-0"
              style={logoStyle}
              data-testid="header-brand-logo"
            />
          ) : (
            <>
              <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-primary text-primary-foreground font-display text-2xl leading-none pt-1">
                {brandName.charAt(0)}
              </div>
              <div className="leading-tight min-w-0">
                <div className="font-display text-lg sm:text-xl lg:text-2xl tracking-tight truncate">{brandName}</div>
                <div className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-accent font-medium mt-0.5 truncate">
                  {brandTagline}
                </div>
              </div>
            </>
          )}
        </Link>

        <nav className="hidden xl:flex items-center gap-6 font-sans text-sm">
          {NAV_ITEMS.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="hover:text-accent transition-fast whitespace-nowrap"
            >
              {it.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-3 min-w-0 md:shrink-0">
          <div className="hidden sm:flex items-center border border-border">
            <button
              data-testid="currency-usd"
              onClick={() => setCurrency("usd")}
              className={`px-2.5 py-1 text-xs font-semibold transition-fast ${currency === "usd" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              USD
            </button>
            <button
              data-testid="currency-inr"
              onClick={() => setCurrency("inr")}
              className={`px-2.5 py-1 text-xs font-semibold transition-fast ${currency === "inr" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              INR
            </button>
          </div>

          <Link
            to="/wishlist"
            data-testid="nav-wishlist"
            className="relative p-2 hover:text-accent transition-fast hidden md:block"
            aria-label="Wishlist"
          >
            <Heart size={20} />
            {wishlist.length > 0 && (
              <span data-testid="wishlist-count" className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[10px] w-5 h-5 flex items-center justify-center font-bold">
                {wishlist.length}
              </span>
            )}
          </Link>

          <Link
            to="/cart"
            data-testid="nav-cart"
            className="relative p-2 hover:text-accent transition-fast"
            aria-label="Cart"
          >
            <ShoppingCart size={20} />
            {cart.length > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[10px] w-5 h-5 flex items-center justify-center font-bold">
                {cart.length}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="p-2 hover:text-accent transition-fast" aria-label="Account">
                  <User size={20} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                {isStaff(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/admin/dashboard")} data-testid="menu-admin">
                    Admin Dashboard
                  </DropdownMenuItem>
                )}
                {(user.role === "super_admin" || user.role === "admin") && (
                  <DropdownMenuItem onClick={() => navigate("/admin/users")} data-testid="menu-users">
                    User Management
                  </DropdownMenuItem>
                )}
                {["super_admin", "admin", "sales", "team_manager"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/admin/enquiries")} data-testid="menu-enquiries">
                    Enquiries
                  </DropdownMenuItem>
                )}
                {(user.role === "super_admin" || user.role === "admin") && (
                  <DropdownMenuItem onClick={() => navigate("/admin/activity")} data-testid="menu-activity">
                    Activity Log
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/purchases")} data-testid="menu-purchases">My Plans</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/wishlist")}>Wishlist</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden md:flex items-center gap-1">
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="nav-login" className="rounded-none">Sign in</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="nav-register" className="rounded-none bg-primary hover:bg-primary/90">
                  Get started
                </Button>
              </Link>
            </div>
          )}

          <button className="xl:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden border-t border-border bg-background px-6 py-6 space-y-3">
          {NAV_ITEMS.map((it) => (
            <Link key={it.to} to={it.to} onClick={() => setMobileOpen(false)} className="block text-lg font-serif">
              {it.label}
            </Link>
          ))}
          {!user && (
            <>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-lg font-serif border-t border-border pt-3">Sign in</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)} className="block text-lg font-serif">Get started</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
